import {
  BBox,
  Vec2,
  arcThroughPoints,
  deg2rad,
  entityLoop,
  makeCircle,
  makeHatch,
  makeLine,
  makePolyline,
  makeRectangle,
  makeSpline,
  makeText,
  pointInLoops,
} from '@lakaran/geometry';
import { Transaction } from '@lakaran/engine';
import { Tool, type ToolField } from './tool';

const MIN_LENGTH = 1e-9;

function directionDegrees(from: Vec2, to: Vec2): number {
  const degrees = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  return (degrees + 360) % 360;
}

export class LineTool extends Tool {
  readonly name = 'LINE';
  readonly title = 'Line';
  readonly aliases = ['line', 'l'];
  private startPoint?: Vec2;
  private latest?: Vec2;

  protected begin(): void {
    this.host.prompt('LINE  Specify first point:');
    this.host.setReference(undefined);
  }

  override get referencePoint(): Vec2 | undefined {
    return this.latest;
  }

  point(p: Vec2): void {
    if (!this.startPoint) {
      this.startPoint = p;
      this.latest = p;
      this.host.setReference(p);
      this.host.prompt('LINE  Specify next point [Enter to finish]:');
      return;
    }
    if (this.startPoint.distanceTo(p) > MIN_LENGTH) {
      this.host.editor.commit(
        new Transaction().add(makeLine(this.startPoint, p, this.host.doc.currentLayerId)),
        'LINE',
      );
    }
    this.startPoint = p;
    this.latest = p;
    this.host.setReference(p);
    this.host.setDraft([]);
  }

  override hover(p: Vec2): void {
    if (!this.startPoint) return;
    this.host.setDraft([makeLine(this.startPoint, p, this.host.doc.currentLayerId)]);
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.startPoint) return null;
    return [
      {
        id: 'length',
        label: 'Length',
        unit: 'mm',
        value: this.startPoint.distanceTo(cursor),
        decimals: 3,
        min: 0,
      },
      {
        id: 'angle',
        label: 'Angle',
        unit: 'deg',
        value: directionDegrees(this.startPoint, cursor),
        decimals: 2,
      },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    if (!this.startPoint) return;
    const length = values['length'] ?? 0;
    const angle = values['angle'] ?? 0;
    if (!(length > 0)) {
      this.host.editor.message('LINE  Length must be greater than zero.');
      return;
    }
    this.point(this.startPoint.add(Vec2.polar(length, deg2rad(angle))));
  }

  override enter(): void {
    this.host.endTool();
  }

  override get enterFinishes(): boolean {
    return true;
  }
}

export class PolylineTool extends Tool {
  readonly name = 'PLINE';
  readonly title = 'Polyline';
  readonly aliases = ['pline', 'pl', 'polyline'];
  private vertices: Vec2[] = [];

  protected begin(): void {
    this.host.prompt('PLINE  Specify start point:');
  }

  override get referencePoint(): Vec2 | undefined {
    return this.vertices[this.vertices.length - 1];
  }

  point(p: Vec2): void {
    this.vertices.push(p);
    this.host.setReference(p);
    this.host.prompt('PLINE  Specify next point [Close/Undo/Enter]:', ['Close', 'Undo']);
    this.host.setDraft([]);
  }

  override hover(p: Vec2): void {
    if (this.vertices.length === 0) return;
    this.host.setDraft([
      makePolyline([...this.vertices, p], false, this.host.doc.currentLayerId),
    ]);
  }

  override input(text: string): boolean {
    const key = text.trim().toLowerCase();
    if (key === 'c' || key === 'close') {
      this.finish(true);
      return true;
    }
    if (key === 'u' || key === 'undo') {
      this.vertices.pop();
      this.host.setReference(this.vertices[this.vertices.length - 1]);
      this.host.setDraft([]);
      this.host.editor.refreshFields();
      return true;
    }
    return false;
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    const last = this.vertices[this.vertices.length - 1];
    if (!last) return null;
    return [
      { id: 'length', label: 'Length', unit: 'mm', value: last.distanceTo(cursor), decimals: 3, min: 0 },
      { id: 'angle', label: 'Angle', unit: 'deg', value: directionDegrees(last, cursor), decimals: 2 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    const last = this.vertices[this.vertices.length - 1];
    if (!last) return;
    const length = values['length'] ?? 0;
    const angle = values['angle'] ?? 0;
    if (!(length > 0)) {
      this.host.editor.message('PLINE  Length must be greater than zero.');
      return;
    }
    this.point(last.add(Vec2.polar(length, deg2rad(angle))));
  }

  override enter(): void {
    this.finish(false);
  }

  override get enterFinishes(): boolean {
    return true;
  }

  override cancel(): void {
    this.vertices = [];
    super.cancel();
  }

  private finish(close: boolean): void {
    if (this.vertices.length >= (close ? 3 : 2)) {
      const entity = makePolyline([...this.vertices], close, this.host.doc.currentLayerId);
      this.host.editor.commit(new Transaction().add(entity), 'PLINE');
    }
    this.vertices = [];
    this.host.endTool();
  }
}

export class SplineTool extends Tool {
  readonly name = 'SPLINE';
  readonly title = 'Spline';
  readonly aliases = ['spline', 'spl', 'nurbs', 'bezier'];

  private points: Vec2[] = [];
  private closed = false;

  protected begin(): void {
    this.host.prompt('SPLINE  Specify first control point [Enter to finish]:');
  }

  override get referencePoint(): Vec2 | undefined {
    return this.points[this.points.length - 1];
  }

  point(p: Vec2): void {
    this.points.push(p);
    this.host.setReference(p);
    const enough = this.points.length >= 3;
    this.host.prompt(
      enough ? 'SPLINE  Specify next control point [Close/Undo/Enter]:' : 'SPLINE  Specify next control point:',
      enough ? ['Close', 'Undo'] : undefined,
    );
    this.host.setDraft([]);
  }

  override hover(p: Vec2): void {
    if (this.points.length === 0) return;
    // Preview the curve the tool would create, using the cursor as the next control point.
    this.host.setDraft([
      makeSpline([...this.points, p], this.host.doc.currentLayerId, { degree: 3 }),
    ]);
  }

  override input(text: string): boolean {
    const key = text.trim().toLowerCase();
    if (key === 'c' || key === 'close') {
      this.closed = true;
      this.finish();
      return true;
    }
    if (key === 'u' || key === 'undo') {
      this.points.pop();
      this.host.setReference(this.points[this.points.length - 1]);
      this.host.setDraft([]);
      return true;
    }
    // `degree` / `deg <2|3>` switches the spline order for the next curve.
    const degree = /^deg(?:ree)?\s*(\d)$/.exec(key);
    if (degree) {
      this.degree = Math.max(1, Math.min(3, Number(degree[1])));
      this.host.editor.message(`SPLINE  Degree ${this.degree}.`);
      return true;
    }
    return false;
  }

  override enter(): void {
    this.finish();
  }

  override get enterFinishes(): boolean {
    return true;
  }

  override cancel(): void {
    this.points = [];
    this.closed = false;
    super.cancel();
  }

  private degree = 3;

  private finish(): void {
    if (this.points.length >= 2) {
      const spline = makeSpline([...this.points], this.host.doc.currentLayerId, {
        degree: this.degree,
        closed: this.closed && this.points.length >= 3,
      });
      this.host.editor.commit(new Transaction().add(spline), 'SPLINE');
    }
    this.points = [];
    this.closed = false;
    this.host.endTool();
  }
}

export class RectangleTool extends Tool {
  readonly name = 'RECT';
  readonly title = 'Rectangle';
  readonly aliases = ['rect', 'rec', 'rectangle'];
  private first?: Vec2;

  protected begin(): void {
    this.host.prompt('RECT  Specify first corner:');
  }

  point(p: Vec2): void {
    if (!this.first) {
      this.first = p;
      this.host.setReference(p);
      this.host.prompt('RECT  Specify opposite corner:');
      return;
    }
    if (Math.abs(p.x - this.first.x) > MIN_LENGTH && Math.abs(p.y - this.first.y) > MIN_LENGTH) {
      this.host.editor.commit(
        new Transaction().add(makeRectangle(this.first, p, this.host.doc.currentLayerId)),
        'RECT',
      );
    }
    this.host.endTool();
  }

  override hover(p: Vec2): void {
    if (!this.first) return;
    this.host.setDraft([makeRectangle(this.first, p, this.host.doc.currentLayerId)]);
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.first) return null;
    return [
      { id: 'width', label: 'Width', unit: 'mm', value: cursor.x - this.first.x, decimals: 3 },
      { id: 'height', label: 'Height', unit: 'mm', value: cursor.y - this.first.y, decimals: 3 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    if (!this.first) return;
    const width = values['width'] ?? 0;
    const height = values['height'] ?? 0;
    if (Math.abs(width) < MIN_LENGTH || Math.abs(height) < MIN_LENGTH) {
      this.host.editor.message('RECT  Width and height must be non-zero.');
      return;
    }
    this.point(Vec2.of(this.first.x + width, this.first.y + height));
  }
}

export class CircleTool extends Tool {
  readonly name = 'CIRCLE';
  readonly title = 'Circle';
  readonly aliases = ['circle', 'c'];
  private center?: Vec2;

  protected begin(): void {
    this.host.prompt('CIRCLE  Specify center point:');
  }

  override get referencePoint(): Vec2 | undefined {
    return this.center;
  }

  point(p: Vec2): void {
    if (!this.center) {
      this.center = p;
      this.host.setReference(p);
      this.host.prompt('CIRCLE  Specify radius or type a value:');
      return;
    }
    this.commit(this.center.distanceTo(p));
  }

  override hover(p: Vec2): void {
    if (!this.center) return;
    const radius = this.center.distanceTo(p);
    if (radius > MIN_LENGTH) {
      this.host.setDraft([makeCircle(this.center, radius, this.host.doc.currentLayerId)]);
    }
  }

  override input(text: string): boolean {
    if (!this.center) return false;
    const radius = Number(text.trim());
    if (!Number.isFinite(radius) || radius <= 0) return false;
    this.commit(radius);
    return true;
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.center) return null;
    return [
      {
        id: 'radius',
        label: 'Radius',
        unit: 'mm',
        value: this.center.distanceTo(cursor),
        decimals: 3,
        min: 0,
      },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    const radius = values['radius'] ?? 0;
    if (!(radius > 0)) {
      this.host.editor.message('CIRCLE  Radius must be greater than zero.');
      return;
    }
    this.commit(radius);
  }

  private commit(radius: number): void {
    if (this.center && radius > MIN_LENGTH) {
      this.host.editor.commit(
        new Transaction().add(makeCircle(this.center, radius, this.host.doc.currentLayerId)),
        'CIRCLE',
      );
    }
    this.host.endTool();
  }
}

export class ArcTool extends Tool {
  readonly name = 'ARC';
  readonly title = 'Arc (3 point)';
  readonly aliases = ['arc', 'a'];
  private points: Vec2[] = [];

  protected begin(): void {
    this.host.prompt('ARC  Specify start point:');
  }

  override get referencePoint(): Vec2 | undefined {
    return this.points[this.points.length - 1];
  }

  point(p: Vec2): void {
    this.points.push(p);
    this.host.setReference(p);
    if (this.points.length === 1) {
      this.host.prompt('ARC  Specify second point:');
    } else if (this.points.length === 2) {
      this.host.prompt('ARC  Specify end point:');
    } else {
      const [a, b, c] = this.points as [Vec2, Vec2, Vec2];
      const arc = arcThroughPoints(a, b, c, this.host.doc.currentLayerId);
      if (arc) this.host.editor.commit(new Transaction().add(arc), 'ARC');
      else this.host.editor.message('ARC  Points are collinear.');
      this.host.endTool();
    }
  }

  override hover(p: Vec2): void {
    if (this.points.length === 1) {
      this.host.setDraft([makeLine(this.points[0]!, p, this.host.doc.currentLayerId)]);
    } else if (this.points.length === 2) {
      const arc = arcThroughPoints(this.points[0]!, this.points[1]!, p, this.host.doc.currentLayerId);
      this.host.setDraft(arc ? [arc] : []);
    }
  }

  override cancel(): void {
    this.points = [];
    super.cancel();
  }
}

export class TextTool extends Tool {
  readonly name = 'TEXT';
  readonly title = 'Text';
  readonly aliases = ['text', 't', 'mtext'];
  private position?: Vec2;

  protected begin(): void {
    this.host.prompt('TEXT  Specify insertion point:');
  }

  point(p: Vec2): void {
    this.position = p;
    this.host.setReference(p);
    this.host.prompt('TEXT  Enter text:');
  }

  override hover(p: Vec2): void {
    if (this.position) return;
    const height = this.host.editor.settings.textHeight;
    this.host.setDraft([makeText(p, 'Text', this.host.doc.currentLayerId, { height })]);
  }

  override input(text: string): boolean {
    if (!this.position) return false;
    const value = text.trim();
    if (!value) return true;
    const height = this.host.editor.settings.textHeight;
    const entity = makeText(this.position, value, this.host.doc.currentLayerId, { height });
    this.host.editor.commit(new Transaction().add(entity), 'TEXT');
    this.host.endTool();
    return true;
  }

  override enter(): void {
    this.host.endTool();
  }
}

export class HatchTool extends Tool {
  readonly name = 'HATCH';
  readonly title = 'Hatch';
  readonly aliases = ['hatch', 'h', 'bhatch'];

  protected begin(): void {
    this.host.prompt('HATCH  Pick an internal point of a closed boundary:');
  }

  point(p: Vec2): void {
    const bounds = BBox.around(p, 1e-6);
    const candidates = this.host.doc.queryVisible(bounds);
    for (const entity of candidates) {
      const loop = entityLoop(entity);
      if (!loop) continue;
      if (!pointInLoops([loop], p)) continue;
      const hatch = makeHatch([loop], this.host.editor.settings.hatchPattern, this.host.doc.currentLayerId);
      this.host.editor.commit(new Transaction().add(hatch), 'HATCH');
      this.host.endTool();
      return;
    }
    this.host.editor.message('HATCH  No closed boundary at that point.');
  }

  override hover(_p: Vec2): void {
    // No preview: hatching is computed on pick.
  }
}

export function createDrawTools(): Array<() => Tool> {
  return [
    () => new LineTool(),
    () => new PolylineTool(),
    () => new RectangleTool(),
    () => new SplineTool(),
    () => new CircleTool(),
    () => new ArcTool(),
    () => new TextTool(),
    () => new HatchTool(),
  ];
}
